import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, LogOut, Mail, Linkedin, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LeadKanban, { STAGES } from "@/components/LeadKanban";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";
import NexusAdminPanel from "@/components/NexusAdminPanel";

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Cyprus",
  cover_image: "",
  author: "Corporaco Editorial",
  read_minutes: 5,
  published: true,
  translations: {},
};

const TRANSLATION_LANGS = [
  { code: "ru", label: "Русский" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "el", label: "Ελληνικά" },
  { code: "pl", label: "Polski" },
  { code: "ar", label: "العربية" },
  { code: "he", label: "עברית" },
  { code: "zh", label: "中文" },
];

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("insights");
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // null or post object
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [stageFilter, setStageFilter] = useState("all");
  const [openLead, setOpenLead] = useState(null);

  const visibleLeads =
    stageFilter === "all"
      ? leads
      : leads.filter((l) => (l.stage || l.status || "new") === stageFilter);

  const moveLeadStage = async (leadId, newStage) => {
    // Optimistic update
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, stage: newStage, status: newStage } : l))
    );
    try {
      const r = await api.put(`/admin/leads/${leadId}`, { stage: newStage });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? r.data : l)));
      const stageLabel = STAGES.find((s) => s.id === newStage)?.label || newStage;
      toast.success(`Moved to ${stageLabel}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
      // Reload to recover from optimistic failure
      loadAll();
    }
  };

  const onLeadUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setOpenLead(updated);
  };

  const onLeadDeleted = (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setOpenLead(null);
  };

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/insights"),
        api.get("/admin/leads"),
      ]);
      setPosts(a.data);
      setLeads(b.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty);
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt,
      content: p.content,
      category: p.category,
      cover_image: p.cover_image || "",
      author: p.author || "Corporaco Editorial",
      read_minutes: p.read_minutes || 5,
      published: p.published,
      translations: p.translations || {},
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm(empty);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug ? slugify(form.slug) : slugify(form.title),
        read_minutes: parseInt(form.read_minutes, 10) || 5,
      };
      if (editing === "new") {
        await api.post("/admin/insights", payload);
        toast.success("Insight published.");
      } else {
        await api.put(`/admin/insights/${editing}`, payload);
        toast.success("Insight updated.");
      }
      cancelEdit();
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this insight? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/insights/${id}`);
      toast.success("Deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen"
      style={{ background: "var(--ink)" }}
    >
      {/* topbar */}
      <header className="border-b border-[var(--line)] sticky top-0 z-30 glass">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="brandmark group" aria-label="Corpora Admin home">
            <img
              src="/brand/corpora-wordmark.png?v=4"
              alt="Corpora Consultants"
              className="brandmark-logo"
              style={{ height: 24 }}
            />
            <span className="overline ml-3 align-middle">Admin</span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--mute)] hidden md:block">
              {user?.email}
            </span>
            <button
              onClick={onLogout}
              className="btn-ghost"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3">Editorial</div>
            <h1 className="font-serif text-5xl md:text-6xl font-light text-[var(--bone)]">
              Newsroom.
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setTab("insights")}
              className={`tag ${tab === "insights" ? "tag-active" : ""}`}
              data-testid="tab-insights"
            >
              Insights ({posts.length})
            </button>
            <button
              onClick={() => setTab("leads")}
              className={`tag ${tab === "leads" ? "tag-active" : ""}`}
              data-testid="tab-leads"
            >
              <Mail size={11} className="inline mr-1.5 -mt-0.5" />
              Leads ({leads.length})
            </button>
            <button
              onClick={() => setTab("nexus")}
              className={`tag ${tab === "nexus" ? "tag-active" : ""}`}
              data-testid="tab-nexus"
              style={tab === "nexus" ? { color: "#C9896E", borderColor: "#C9896E" } : {}}
            >
              Nexus
            </button>
          </div>
        </div>

        {tab === "insights" && !editing && (
          <>
            <div className="flex justify-end gap-3 mb-6">
              <button
                onClick={async () => {
                  setGenerating(true);
                  try {
                    const r = await api.post("/admin/insights/auto-generate");
                    toast.success(`Generated: ${r.data.insight.title}`);
                    await loadAll();
                  } catch {
                    toast.error("Auto-generation failed");
                  } finally {
                    setGenerating(false);
                  }
                }}
                disabled={generating}
                className="btn-ghost disabled:opacity-50"
                data-testid="auto-generate-btn"
              >
                {generating ? "Generating…" : "✨ Auto-generate briefing"}
              </button>
              <button onClick={startNew} className="btn-primary" data-testid="new-insight-btn">
                <Plus size={14} /> New insight
              </button>
            </div>
            <div className="border border-[var(--line)]" data-testid="insights-table">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)]">
                <div className="col-span-5">Title</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Updated</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              {posts.map((p) => {
                const publicUrl = `${window.location.origin}/api/share/${p.slug}`;
                const liShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;
                const shared = Boolean(p.linkedin_shared_at);
                return (
                <div
                  key={p.id}
                  className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[var(--line)] items-center"
                  data-testid={`insight-row-${p.slug}`}
                >
                  <div className="col-span-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-serif text-lg text-[var(--bone)] leading-tight">
                        {p.title}
                      </div>
                      {p.auto_generated && (
                        <span
                          className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
                          style={{
                            background: "rgba(190,170,140,0.12)",
                            color: "var(--pewter)",
                            border: "1px solid var(--line-2)",
                            borderRadius: 3,
                          }}
                          data-testid={`badge-auto-${p.slug}`}
                        >
                          AI
                        </span>
                      )}
                      {shared && (
                        <span
                          className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 inline-flex items-center gap-1"
                          style={{
                            background: "rgba(127,176,138,0.12)",
                            color: "#7fb08a",
                            border: "1px solid rgba(127,176,138,0.35)",
                            borderRadius: 3,
                          }}
                          data-testid={`badge-shared-${p.slug}`}
                        >
                          <Linkedin size={10} /> Shared
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)] mt-1">
                      /{p.slug} {p.published ? "" : "· DRAFT"}
                    </div>
                  </div>
                  <div className="col-span-2 text-[var(--bone-2)] text-sm">{p.category}</div>
                  <div className="col-span-2 font-mono text-[11px] text-[var(--mute)]">
                    {new Date(p.updated_at).toLocaleDateString("en-GB")}
                  </div>
                  <div className="col-span-3 flex justify-end gap-2 items-center">
                    <a
                      href={liShareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        api.post(`/admin/insights/${p.id}/linkedin-shared`)
                          .then(() => loadAll())
                          .catch(() => {});
                      }}
                      title="Share on LinkedIn"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
                      style={{
                        color: "var(--bone-2)",
                        border: "1px solid var(--line-2)",
                        borderRadius: 4,
                      }}
                      data-testid={`linkedin-share-${p.slug}`}
                    >
                      <Linkedin size={12} />
                      Share
                    </a>
                    <a
                      href={`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(publicUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open LinkedIn Post Inspector — refresh cached OG preview"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
                      style={{
                        color: "var(--mute)",
                        border: "1px solid var(--line-2)",
                        borderRadius: 4,
                      }}
                      data-testid={`linkedin-inspect-${p.slug}`}
                    >
                      <Search size={12} />
                      Inspect
                    </a>
                    <button
                      onClick={() => startEdit(p)}
                      className="text-[var(--bone-2)] hover:text-[var(--pewter)] p-1.5"
                      data-testid={`edit-${p.slug}`}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(p.id)}
                      className="text-[var(--bone-2)] hover:text-[#E07A7A] p-1.5"
                      data-testid={`delete-${p.slug}`}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                );
              })}
              {posts.length === 0 && (
                <div className="px-6 py-10 text-center text-[var(--mute)]">
                  No insights yet. Click "New insight" to publish.
                </div>
              )}
            </div>
          </>
        )}

        {tab === "insights" && editing && (
          <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-12 gap-8" data-testid="insight-form">
            <div className="md:col-span-8 space-y-6">
              <div>
                <label className="overline block mb-2">Title *</label>
                <input name="title" required value={form.title} onChange={onChange} className="field" data-testid="form-title" />
              </div>
              <div>
                <label className="overline block mb-2">Slug</label>
                <input
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  placeholder={slugify(form.title || "auto-from-title")}
                  className="field font-mono"
                  data-testid="form-slug"
                />
              </div>
              <div>
                <label className="overline block mb-2">Excerpt *</label>
                <textarea
                  name="excerpt"
                  required
                  rows={3}
                  value={form.excerpt}
                  onChange={onChange}
                  className="field"
                  data-testid="form-excerpt"
                />
              </div>
              <div>
                <label className="overline block mb-2">Content (Markdown-lite) *</label>
                <textarea
                  name="content"
                  required
                  rows={16}
                  value={form.content}
                  onChange={onChange}
                  className="field font-mono text-sm"
                  data-testid="form-content"
                />
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute-2)] mt-2">
                  Tip — start a line with "- " for bulleted points.
                </p>
              </div>

              {/* TRANSLATIONS EDITOR */}
              <div className="border-t border-[var(--line)] pt-8 mt-4" data-testid="translations-editor">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="overline mb-1">Translations</div>
                    <p className="font-mono text-[10px] text-[var(--mute-2)] uppercase tracking-[0.2em]">
                      Optional — falls back to English when missing
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {TRANSLATION_LANGS.map((lang) => {
                    const tr = (form.translations && form.translations[lang.code]) || {};
                    const hasContent = !!(tr.title || tr.excerpt || tr.content);
                    return (
                      <details
                        key={lang.code}
                        className="border border-[var(--line)]"
                        open={hasContent}
                      >
                        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-[var(--bone-2)] hover:text-[var(--bone)]">
                          <span className="flex items-center gap-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">
                              {lang.code.toUpperCase()}
                            </span>
                            <span className="font-serif text-[15px]">{lang.label}</span>
                          </span>
                          {hasContent && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">
                              · filled
                            </span>
                          )}
                        </summary>
                        <div className="px-4 pb-4 space-y-3" data-testid={`translation-${lang.code}`}>
                          <input
                            placeholder={`Title — ${lang.label}`}
                            value={tr.title || ""}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                translations: {
                                  ...f.translations,
                                  [lang.code]: { ...tr, title: e.target.value },
                                },
                              }))
                            }
                            className="field"
                            dir={lang.code === "ar" ? "rtl" : "ltr"}
                          />
                          <textarea
                            placeholder={`Excerpt — ${lang.label}`}
                            value={tr.excerpt || ""}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                translations: {
                                  ...f.translations,
                                  [lang.code]: { ...tr, excerpt: e.target.value },
                                },
                              }))
                            }
                            rows={2}
                            className="field"
                            dir={lang.code === "ar" ? "rtl" : "ltr"}
                          />
                          <textarea
                            placeholder={`Content — ${lang.label}`}
                            value={tr.content || ""}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                translations: {
                                  ...f.translations,
                                  [lang.code]: { ...tr, content: e.target.value },
                                },
                              }))
                            }
                            rows={8}
                            className="field font-mono text-sm"
                            dir={lang.code === "ar" ? "rtl" : "ltr"}
                          />
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="md:col-span-4 space-y-6">
              <div>
                <label className="overline block mb-2">Category *</label>
                <select name="category" value={form.category} onChange={onChange} className="field" data-testid="form-category">
                  <option style={{ background: "var(--ink)" }}>Cyprus</option>
                  <option style={{ background: "var(--ink)" }}>EU</option>
                  <option style={{ background: "var(--ink)" }}>Tax</option>
                  <option style={{ background: "var(--ink)" }}>Banking</option>
                  <option style={{ background: "var(--ink)" }}>Compliance</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Cover image URL</label>
                <input name="cover_image" value={form.cover_image} onChange={onChange} className="field" data-testid="form-cover" />
                {form.cover_image && (
                  <img
                    src={form.cover_image}
                    alt=""
                    className="mt-3 w-full aspect-[4/3] object-cover border border-[var(--line)]"
                  />
                )}
              </div>
              <div>
                <label className="overline block mb-2">Author</label>
                <input name="author" value={form.author} onChange={onChange} className="field" />
              </div>
              <div>
                <label className="overline block mb-2">Read time (min)</label>
                <input
                  type="number"
                  name="read_minutes"
                  value={form.read_minutes}
                  onChange={onChange}
                  className="field"
                  min={1}
                />
              </div>
              <label className="flex items-center gap-3 text-[var(--bone-2)]">
                <input
                  type="checkbox"
                  name="published"
                  checked={form.published}
                  onChange={onChange}
                  data-testid="form-published"
                />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Published
                </span>
              </label>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-[var(--line)]">
                <button type="submit" disabled={saving} className="btn-primary" data-testid="form-save">
                  {saving ? "Saving…" : editing === "new" ? "Publish" : "Save changes"}
                </button>
                <button type="button" onClick={cancelEdit} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {tab === "leads" && (
          <div data-testid="leads-crm">
            {/* Language routing summary */}
            {leads.length > 0 && (() => {
              const langCounts = leads.reduce((m, l) => {
                const k = l.language || "en";
                m[k] = (m[k] || 0) + 1;
                return m;
              }, {});
              const codeToLabel = {
                en: "EN", ru: "RU", de: "DE", fr: "FR",
                el: "EL", ar: "AR", zh: "ZH", he: "HE",
              };
              return (
                <div
                  className="mb-6 flex flex-wrap items-center gap-3"
                  data-testid="leads-routing"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mr-1">
                    Routing
                  </span>
                  {Object.entries(langCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([code, n]) => (
                      <span key={code} className="tag" style={{ padding: "4px 10px" }}>
                        {codeToLabel[code] || code.toUpperCase()} · {n}
                      </span>
                    ))}
                </div>
              );
            })()}

            {/* Stage filter tabs */}
            <div className="mb-6 flex flex-wrap gap-2" data-testid="leads-filter">
              <button
                onClick={() => setStageFilter("all")}
                data-testid="leads-filter-all"
                className={`tag ${stageFilter === "all" ? "tag-active" : ""}`}
              >
                All ({leads.length})
              </button>
              {STAGES.map((s) => {
                const count = leads.filter(
                  (l) => (l.stage || l.status || "new") === s.id
                ).length;
                if (count === 0 && stageFilter !== s.id) return null;
                return (
                  <button
                    key={s.id}
                    onClick={() => setStageFilter(s.id)}
                    data-testid={`leads-filter-${s.id}`}
                    className={`tag ${stageFilter === s.id ? "tag-active" : ""}`}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: s.color,
                        marginRight: 6,
                      }}
                    />
                    {s.label} ({count})
                  </button>
                );
              })}
            </div>

            {leads.length === 0 ? (
              <div
                className="px-6 py-16 text-center text-[var(--mute)] border border-[var(--line)]"
                data-testid="leads-empty"
              >
                No leads yet. They will appear here as soon as someone submits the contact form.
              </div>
            ) : (
              <LeadKanban
                leads={visibleLeads}
                onMove={moveLeadStage}
                onOpen={(lead) => setOpenLead(lead)}
              />
            )}

            {openLead && (
              <LeadDetailDrawer
                lead={openLead}
                onClose={() => setOpenLead(null)}
                onUpdated={onLeadUpdated}
                onDeleted={onLeadDeleted}
              />
            )}
          </div>
        )}

        {tab === "nexus" && <NexusAdminPanel />}
      </div>
    </div>
  );
}
